'use client';

import * as React from 'react';

import { Loader2, Search, X } from 'lucide-react';

import { Button } from '../../base/Button';
import { cn } from '../../lib/utils';
import { DebouncedInput } from '../Form/DebouncedInput/DebouncedInput';

export const SearchInput = React.forwardRef<
  HTMLDivElement,
  {
    searchResults: {
      id: string | number;
      title: string;
      category: string;
      customObject?: Record<string, any>;
    }[];
    width?: string;
    height?: string;
    placeholder?: string;
    onSelect?: (result: {
      id: string | number;
      title: string;
      category: string;
      customObject?: Record<string, any>;
    }) => void;
    onInputChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    debounceTime?: number;
    loading?: boolean;
    loadingText?: string;
  }
>(
  (
    {
      searchResults,
      width = '400px',
      height = '40px',
      placeholder = 'Search...',
      onSelect,
      onInputChange,
      debounceTime = 300,
      loading = false,
      loadingText = 'Loading...',
    },
    ref,
  ) => {
    const [open, setOpen] = React.useState(false);
    const [inputValue, setInputValue] = React.useState('');
    const [focusedIndex, setFocusedIndex] = React.useState(-1);
    const inputRef = React.useRef<HTMLInputElement>(null);
    const dropdownRef = React.useRef<HTMLDivElement>(null);
    const itemRefs = React.useRef<(HTMLButtonElement | null)[]>([]);

    // Flatten results for keyboard navigation
    const flatResults = React.useMemo(() => {
      if (!inputValue.trim()) return searchResults;

      // Filter as user types
      return searchResults.filter((item) =>
        item.title.toLowerCase().includes(inputValue.toLowerCase()),
      );
    }, [inputValue, searchResults]);

    // Group results by category for display
    const groupedResults = React.useMemo(() => {
      return flatResults.reduce((acc, item) => {
        if (!acc[item.category]) {
          acc[item.category] = [];
        }
        acc[item.category].push(item);
        return acc;
      }, {} as Record<string, typeof searchResults>);
    }, [flatResults]);

    const hasResults = flatResults.length > 0;

    // Reset focused index when input changes or dropdown closes
    React.useEffect(() => {
      setFocusedIndex(-1);
    }, [inputValue, open]);

    // Initialize refs array when results change
    React.useEffect(() => {
      itemRefs.current = itemRefs.current.slice(0, flatResults.length);
    }, [flatResults]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setInputValue(value);
      onInputChange?.(e);
    };

    const handleSelect = (result: {
      id: string | number;
      title: string;
      category: string;
    }) => {
      setInputValue(result.title);
      setOpen(false);
      onSelect?.(result);
    };

    const handleClear = () => {
      setInputValue('');
      inputRef.current?.focus();
    };

    const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!open || !hasResults) return;

      // Down arrow moves focus to the dropdown
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusedIndex(0);
        itemRefs.current[0]?.focus();
      } else if (e.key === 'Escape') {
        setOpen(false);
      }
    };

    const handleItemKeyDown = (
      e: React.KeyboardEvent<HTMLButtonElement>,
      index: number,
    ) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const nextIndex = Math.min(index + 1, flatResults.length - 1);
        setFocusedIndex(nextIndex);
        itemRefs.current[nextIndex]?.focus();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (index === 0) {
          // Move focus back to input when at the first item
          setFocusedIndex(-1);
          inputRef.current?.focus();
        } else {
          const prevIndex = Math.max(index - 1, 0);
          setFocusedIndex(prevIndex);
          itemRefs.current[prevIndex]?.focus();
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setOpen(false);
        inputRef.current?.focus();
      } else if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleSelect(flatResults[index]);
      }
    };

    // Close dropdown when clicking outside
    React.useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target as Node) &&
          inputRef.current &&
          !inputRef.current.contains(event.target as Node)
        ) {
          setOpen(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, []);

    const isFullWidth = width === '100%' || width === '100vw';
    return (
      <div
        ref={ref}
        className={cn('relative w-full', !isFullWidth && 'max-w-sm')}
        style={{ width, height }}
      >
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <DebouncedInput
            debounceTime={debounceTime}
            ref={inputRef}
            type="text"
            placeholder={placeholder}
            className="pl-8 pr-8 bg-white dark:bg-gray-800"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleInputKeyDown}
            onFocus={() => setOpen(true)}
          />
          <div className="absolute right-0 top-0 h-full flex items-center pr-2">
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            ) : inputValue ? (
              <Button
                variant="ghost"
                size="sm"
                className="h-full px-3 py-0"
                onClick={handleClear}
              >
                <X className="h-4 w-4" />
                <span className="sr-only">Clear</span>
              </Button>
            ) : null}
          </div>
        </div>

        {open && (hasResults || loading) && (
          <div
            ref={dropdownRef}
            className="absolute z-50 mt-1 w-full rounded-md border bg-popover p-0 text-popover-foreground shadow-md outline-none animate-in fade-in-0 zoom-in-95"
          >
            <div className="max-h-[300px] overflow-auto p-1">
              {loading ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  <span className="ml-2 text-sm text-muted-foreground">
                    {loadingText}
                  </span>
                </div>
              ) : (
                Object.entries(groupedResults).map(([category, items]) => (
                  <div key={category} className="mb-2">
                    <div className="px-2 py-1.5 text-sm font-medium text-muted-foreground">
                      {category}
                    </div>
                    <div>
                      {items.map((item) => {
                        // Find the index in the flat array for keyboard navigation
                        const flatIndex = flatResults.findIndex(
                          (r) => r.id === item.id,
                        );
                        return (
                          <button
                            key={item.id}
                            ref={(el) => (itemRefs.current[flatIndex] = el)}
                            className={`w-full rounded-sm px-2 py-1.5 text-sm text-left ${
                              focusedIndex === flatIndex
                                ? 'bg-accent text-accent-foreground'
                                : 'hover:bg-accent hover:text-accent-foreground'
                            }`}
                            onClick={() => handleSelect(item)}
                            onKeyDown={(e) => handleItemKeyDown(e, flatIndex)}
                            type="button"
                          >
                            {item.title}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    );
  },
);

SearchInput.displayName = 'SearchInput';
